import {
	Button,
	Chip,
} from '@mui/material';
import {
	PlayCircle,
	RefreshCw,
} from 'lucide-react';

import { BaseModal } from '@/Components/Modals/BaseModal';
import { BackdropLoader } from '@/Components/Loaders';
import {
	formatStatusLabel,
	getTrainingGuidance,
	normalizeStatusValue,
	statusChipClass,
} from '../shapeModelLibrary.helpers';

const TrainingStatusModal = ({
	open,
	item,
	data,
	readinessLoading,
	prepareLoading,
	trainLoading,
	canPrepareTrainingData,
	canStartTraining,
	onClose,
	onPrepare,
	onRefresh,
	onStartTraining,
}) => {
	const readinessGuide = getTrainingGuidance(data);
	const isTrainingFailed =
		normalizeStatusValue(data?.trainingStatus) === 'failed';
	const isBusy = readinessLoading || prepareLoading || trainLoading;

	return (
		<BaseModal
			open={open}
			heading={`Training Status${item?.shapeName ? ` - ${item.shapeName}` : ''}`}
			width='42rem'
			height='38rem'
			handleClose={onClose}
			hideFooter
		>
			<div className='relative flex h-full flex-col items-stretch gap-4 overflow-y-auto pt-3'>
				<BackdropLoader isLoading={isBusy} showProgress />
				<div className='flex flex-wrap items-center justify-between gap-3'>
					<div className='flex flex-wrap items-center gap-3'>
						<Chip
							label={formatStatusLabel(data?.modelStatus ?? 'Checking')}
							className={`!h-8 !px-1 !text-sm !font-semibold ${statusChipClass(data?.modelStatus)}`}
						/>
						{data?.exportStatus && (
							<Chip
								label={`Data Prep: ${formatStatusLabel(data.exportStatus)}`}
								className={`!h-8 !px-1 !text-sm !font-semibold ${statusChipClass(data?.exportStatus)}`}
							/>
						)}
						{data?.trainingStatus && (
							<Chip
								label={`Training: ${formatStatusLabel(data.trainingStatus)}`}
								className={`!h-8 !px-1 !text-sm !font-semibold ${statusChipClass(data?.trainingStatus)}`}
							/>
						)}
					</div>
					<div className='flex flex-wrap items-center gap-2'>
						<Button
							className='!rounded-md !border !border-slate-300 !bg-white !px-4 !py-2 !font-medium !normal-case !text-slate-700 hover:!border-slate-400 hover:!bg-slate-50 disabled:!border-slate-200 disabled:!bg-slate-100 disabled:!text-slate-400'
							startIcon={<RefreshCw size={18} />}
							disabled={isBusy || !item}
							onClick={onRefresh}
						>
							Refresh Status
						</Button>
						{canPrepareTrainingData && (
							<Button
								className='!rounded-md !border !border-slate-300 !bg-white !px-4 !py-2 !font-medium !normal-case !text-slate-700 hover:!border-slate-400 hover:!bg-slate-50 disabled:!border-slate-200 disabled:!bg-slate-100 disabled:!text-slate-400'
								startIcon={<RefreshCw size={18} />}
								disabled={isBusy}
								onClick={onPrepare}
							>
								Retry Data Prep
							</Button>
						)}
						<Button
							className='!rounded-md !bg-uy-color !px-4 !py-2 !font-medium !normal-case !text-black hover:!bg-yellow-500 disabled:!bg-slate-200 disabled:!text-slate-500'
							startIcon={<PlayCircle size={18} />}
							disabled={isBusy || !canStartTraining}
							onClick={onStartTraining}
						>
							Start Training
						</Button>
					</div>
				</div>
				<div className='grid grid-cols-1 gap-3 text-sm font-semibold text-slate-700 md:grid-cols-2'>
					<div className='rounded-md border border-slate-200 bg-slate-50 px-3 py-2'>
						<span>Can start training:</span>
						<span className='ml-1'>
							{data?.canStartTraining ? 'Yes' : 'No'}
						</span>
					</div>
					<div className='rounded-md border border-slate-200 bg-slate-50 px-3 py-2'>
						<span>Backend run status:</span>
						<span className='ml-1'>
							{formatStatusLabel(data?.trainingRunStatus)}
						</span>
					</div>
					<div className='rounded-md border border-slate-200 bg-slate-50 px-3 py-2'>
						<span>Model version:</span>
						<span className='ml-1'>{data?.modelVersion ?? '-'}</span>
					</div>
					<div className='rounded-md border border-slate-200 bg-slate-50 px-3 py-2'>
						<span>Target version:</span>
						<span className='ml-1'>
							{data?.targetModelVersion ?? '-'}
						</span>
					</div>
					<div className='rounded-md border border-slate-200 bg-slate-50 px-3 py-2'>
						<span>CVAT project:</span>
						<span className='ml-1'>{data?.cvatProjectId ?? '-'}</span>
					</div>
					<div className='rounded-md border border-slate-200 bg-slate-50 px-3 py-2'>
						<span>Task ID:</span>
						<span className='ml-1'>{data?.taskId ?? '-'}</span>
					</div>
				</div>
				<div className='rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700'>
					{readinessGuide}
				</div>
				{data?.reason && (
					<div className='rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700'>
						<span className='font-semibold text-slate-900'>Reason:</span>
						<span className='ml-1'>
							{formatStatusLabel(data.reason)}
						</span>
					</div>
				)}
				{isTrainingFailed && data?.trainingError && (
					<div className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700'>
						<span className='font-semibold'>Training error:</span>
						<span className='ml-1'>
							{data.trainingError}
						</span>
					</div>
				)}
			</div>
		</BaseModal>
	);
};

export default TrainingStatusModal;
